import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { Application } from '~/data/useApplication';

type ManualPaymentParams = {
  description: string;
  amountReceived: number;
  dateReceived: string;
};

export function useManualPaymentActivity() {
  const process = useAppProcessing();
  const request = useRequest();
  const queryClient = useQueryClient();
  const [isBusy, setIsBusy] = React.useState(false);

  const { mutateAsync } = useMutation(
    ['markAsPaidManual', process.application.id],
    async (params: ManualPaymentParams) => {
      return request(`${config.apiHost}/private/v1/applications/${process.application.id}/manual-payment`, {
        method: 'POST',
        body: {
          data: {
            type: 'manual_payment',
            attributes: {
              description: params.description,
              amountReceived: params.amountReceived,
              dateReceived: params.dateReceived,
              humanReadableId: process.application.humanReadableId,
            },
          },
        },
        isExpectedResponse: (res: unknown): res is unknown => Boolean(res && typeof res === 'object'),
      });
    }
  );

  async function submitManualPayment(params: ManualPaymentParams) {
    setIsBusy(true);
    try {
      await mutateAsync(params);
      const queryKey = ['application', process.application.id];
      queryClient.setQueryData<Application>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          applicationFee: {
            ...prev.applicationFee,
            paymentStatus: 'PAID',
            amountReceived: params.amountReceived,
            isManualPayment: true,
            description: params.description,
            dateReceived: params.dateReceived,
          },
        };
      });
    } finally {
      setIsBusy(false);
    }
  }

  return {
    isBusy,
    submitManualPayment,
    completeProcess: () => process.completeProcess(),
  };
}
